import { cn } from '~/src/lib/utils'

interface ComparisonColumn {
  slug: string
  name: string
  isHighlighted: boolean
}

interface ComparisonRow {
  label: string
  values: Array<string | null>
}

interface SeoReportComparisonTableProps {
  title: string
  columns: ComparisonColumn[]
  rows: ComparisonRow[]
}

export function SeoReportComparisonTable({
  title,
  columns,
  rows,
}: SeoReportComparisonTableProps) {
  if (columns.length === 0 || rows.length === 0) return null

  return (
    <section className='space-y-6'>
      <h2 className='font-display text-2xl font-bold text-foreground'>
        {title}
      </h2>

      <div
        className='overflow-x-auto rounded-2xl border border-border bg-white scrollbar-none'
        style={{ overscrollBehaviorX: 'none', touchAction: 'pan-x pan-y' }}
      >
        <table
          className='w-full table-fixed border-collapse text-sm'
          style={{ minWidth: `${Math.max(600, columns.length * 160 + 160)}px` }}
        >
          <caption className='sr-only'>{title}</caption>
          <thead>
            <tr>
              <th
                scope='col'
                className='border-b border-black/10 bg-[#F5F4F8] p-4' /* color-token-exception: #F5F4F8 light purple tint from Figma table header spec; no semantic token */
              >
                <span className='sr-only'>Specification</span>
              </th>
              {columns.map((col) => (
                <th
                  key={col.slug}
                  scope='col'
                  style={{ width: `${100 / (columns.length + 1)}%` }}
                  className={cn(
                    'border-b border-black/10 px-4 py-3 text-left text-xs font-medium text-foreground md:text-sm',
                    col.isHighlighted
                      ? 'bg-[rgba(244,243,255,0.3)]' /* color-token-exception: Figma highlight tint */
                      : 'bg-[#F5F4F8]' /* color-token-exception: same table header tint */
                  )}
                >
                  <span className='line-clamp-2 text-balance'>{col.name}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              const isLastRow = rowIndex === rows.length - 1
              return (
                <tr key={`${row.label}-${rowIndex}`}>
                  <th
                    scope='row'
                    className={cn(
                      'p-4 text-left text-xs font-medium text-foreground md:text-sm',
                      !isLastRow && 'border-b border-border'
                    )}
                  >
                    {row.label}
                  </th>
                  {row.values.map((value, colIndex) => {
                    const col = columns[colIndex]
                    const isHighlighted = col?.isHighlighted ?? false
                    return (
                      <td
                        key={`${row.label}-${rowIndex}-${col?.slug ?? colIndex}`}
                        className={cn(
                          'p-2.5 text-left text-xs font-normal text-foreground-tertiary md:text-sm',
                          !isLastRow && 'border-b border-border',
                          isHighlighted &&
                            'bg-[rgba(244,243,255,0.3)]' /* color-token-exception: Figma highlight tint; no semantic token for this faint lavender */
                        )}
                      >
                        {value ?? '\u2014'}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}
